import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  Section, 
  Card, 
  Button,
  FadeInUp,
  SlideIn,
  StaggerContainer,
  StaggerItem,
} from '../../../components/common';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { EXCHANGE_BRITISH_ISLES, EXCHANGE_EUROPEAN_CLASSICS, ONLINE_COURSES_HERO } from '../../../constants';

// Fix para ícones do Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icon
const createCustomIcon = (color: string) => L.divIcon({
  className: 'custom-marker',
  html: `<div style="
    background: ${color};
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
  "><i class="ri-map-pin-2-fill" style="color: white; font-size: 12px;"></i></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Expedições - todas já realizadas
const EXPEDITIONS = [
  {
    id: 1,
    name: 'British Isles 2025',
    year: 2025,
    status: 'realizada',
    date: 'Janeiro 2025',
    duration: '21 dias',
    countries: ['Irlanda', 'Escócia', 'Inglaterra'],
    cities: ['Dublin', 'Edinburgh', 'Londres'],
    participants: 12,
    color: '#6B8E23',
    route: [
      { name: 'Dublin', coords: [53.3498, -6.2603] as [number, number], highlight: 'Explore os pubs históricos de Temple Bar, visite a icônica Trinity College e deslumbre-se com as falésias de Cliffs of Moher' },
      { name: 'Edinburgh', coords: [55.9533, -3.1883] as [number, number], highlight: 'Caminhe pela medieval Royal Mile, aventure-se pelas Highlands escocesas e descubra castelos centenários' },
      { name: 'Londres', coords: [51.5074, -0.1278] as [number, number], highlight: 'Contemple o majestoso Big Ben, explore o vibrante Camden Market e viva a cultura dos pubs britânicos' },
    ],
    coverImage: EXCHANGE_BRITISH_ISLES.cover.url,
    gallery: EXCHANGE_BRITISH_ISLES.gallery.map(img => img.url),
  },
  {
    id: 2,
    name: 'British Isles 2026',
    year: 2026,
    status: 'realizada',
    date: 'Fevereiro 2026',
    duration: '21 dias',
    countries: ['Irlanda', 'Escócia', 'Inglaterra'],
    cities: ['Dublin', 'Edinburgh', 'Londres'],
    participants: 14,
    color: '#6B8E23',
    route: [
      { name: 'Dublin', coords: [53.3498, -6.2603] as [number, number], highlight: 'Explore os pubs históricos de Temple Bar, visite a icônica Trinity College e deslumbre-se com as falésias de Cliffs of Moher' },
      { name: 'Edinburgh', coords: [55.9533, -3.1883] as [number, number], highlight: 'Caminhe pela medieval Royal Mile, aventure-se pelas Highlands escocesas e descubra castelos centenários' },
      { name: 'Londres', coords: [51.5074, -0.1278] as [number, number], highlight: 'Contemple o majestoso Big Ben, explore o vibrante Camden Market e viva a cultura dos pubs britânicos' },
    ],
    coverImage: EXCHANGE_BRITISH_ISLES.cover.url,
    gallery: EXCHANGE_BRITISH_ISLES.gallery.map(img => img.url),
  },
  {
    id: 3,
    name: 'Eastern Europe',
    year: 2026,
    status: 'realizada',
    date: 'Novembro 2026',
    duration: '18 dias',
    countries: ['Alemanha', 'República Tcheca', 'Polônia'],
    cities: ['Berlim', 'Dresden', 'Praga', 'Cracóvia', 'Varsóvia'],
    participants: 10,
    color: '#C4A35A',
    route: [
      { name: 'Berlim', coords: [52.5200, 13.4050] as [number, number], highlight: 'Visite os resquícios do histórico Muro de Berlim, cruze o icônico Portão de Brandemburgo e explore museus de classe mundial' },
      { name: 'Dresden', coords: [51.0504, 13.7373] as [number, number], highlight: 'Admire a magnífica Frauenkirche e encante-se com a arquitetura barroca meticulosamente reconstruída' },
      { name: 'Praga', coords: [50.0755, 14.4378] as [number, number], highlight: 'Atravesse a mística Ponte Carlos, descubra o grandioso Castelo de Praga e aproveite a vibrante vida noturna' },
      { name: 'Cracóvia', coords: [50.0647, 19.9450] as [number, number], highlight: 'Passeie pelo charmoso centro histórico, desça às impressionantes minas de sal e reflita em Auschwitz' },
      { name: 'Varsóvia', coords: [52.2297, 21.0122] as [number, number], highlight: 'Conheça a cidade velha magnificamente reconstruída e mergulhe na rica e resiliente história polonesa' },
    ],
    coverImage: EXCHANGE_EUROPEAN_CLASSICS.cover.url,
    gallery: EXCHANGE_EUROPEAN_CLASSICS.gallery.map(img => img.url),
  },
];

// Benefícios - foco em economia e experiência
const TRANSFORMATION_BENEFITS = [
  {
    icon: 'ri-wallet-3-line',
    title: 'Economia Real',
    stat: 'Até 40%',
    desc: 'mais barato que viajar sozinho'
  },
  {
    icon: 'ri-team-line',
    title: 'Grupo Pequeno',
    stat: '10-15',
    desc: 'pessoas por expedição'
  },
  {
    icon: 'ri-home-smile-line',
    title: 'Hostels',
    stat: '100%',
    desc: 'hospedagem estilo mochileiro'
  },
  {
    icon: 'ri-chat-smile-3-line',
    title: 'Inglês 24h',
    stat: 'Imersão',
    desc: 'praticando o tempo todo'
  },
];

// Animated Map component
function AnimatedRoute({ route, color }: { route: typeof EXPEDITIONS[0]['route'], color: string }) {
  const map = useMap();
  
  useEffect(() => {
    const bounds = L.latLngBounds(route.map(r => r.coords));
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [route, map]);

  const pathCoords = route.map(r => r.coords);

  return (
    <>
      <Polyline 
        positions={pathCoords} 
        color={color}
        weight={3}
        dashArray="10, 10"
        opacity={0.8}
      />
      {route.map((stop, index) => (
        <Marker 
          key={stop.name} 
          position={stop.coords}
          icon={createCustomIcon(color)}
        >
          <Popup>
            <div className="text-center p-1">
              <p className="font-bold text-earth-800">{stop.name}</p>
              <p className="text-earth-600 text-xs mt-1">{stop.highlight}</p>
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  );
}

export default function Exchange() {
  usePageTitle('Intercâmbio');
  const [selectedExpedition, setSelectedExpedition] = useState(EXPEDITIONS[0]);
  const [activeGalleryImage, setActiveGalleryImage] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  return (
    <>
      {/* Hero - Clean and Minimal */}
      <section className="relative min-h-[90vh] bg-earth-900 overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img 
            src={ONLINE_COURSES_HERO[0].url}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-earth-900 via-earth-900/80 to-earth-900/40" />
        </div>

        <div className="container-custom relative z-10 pt-24 sm:pt-32 pb-16 sm:pb-20 min-h-[90vh] flex items-center">
          <div className="max-w-2xl">
            {/* Simple badge */}
            <motion.div 
              className="inline-flex items-center gap-2 bg-olive-600/90 text-white px-3 py-1.5 rounded-full text-xs sm:text-sm mb-4 sm:mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <i className="ri-compass-3-line"></i>
              Intercâmbios em grupo
            </motion.div>
            
            <motion.h1 
              className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Viaje barato.<br />
              <span className="text-olive-400">Aprenda de verdade.</span>
            </motion.h1>
            
            <motion.p 
              className="text-base sm:text-lg text-sand-200 mb-6 sm:mb-8 leading-relaxed max-w-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              Hostels, transporte compartilhado e roteiro otimizado. 
              Você viaja como mochileiro, pratica inglês o tempo todo 
              e ainda economiza.
            </motion.p>

            {/* Key benefits - simple */}
            <motion.div 
              className="flex flex-wrap gap-3 sm:gap-4 mb-6 sm:mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {[
                { icon: 'ri-money-dollar-circle-line', text: 'Custo compartilhado' },
                { icon: 'ri-group-line', text: 'Turmas pequenas' },
                { icon: 'ri-map-pin-line', text: 'Roteiro planejado' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-2 text-sand-300 text-sm">
                  <i className={`${item.icon} text-olive-400`}></i>
                  {item.text}
                </div>
              ))}
            </motion.div>

            <motion.div 
              className="flex flex-col sm:flex-row gap-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Button 
                href="/contato" 
                size="lg" 
                icon="ri-arrow-right-line"
              >
                Ver próxima expedição
              </Button>
              <Button 
                href="#expeditions" 
                variant="outline" 
                size="lg"
                className="border-white/30 text-white hover:bg-white hover:text-earth-900"
              >
                Roteiros anteriores
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator - simpler */}
        <motion.div 
          className="absolute bottom-6 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <i className="ri-arrow-down-s-line text-2xl text-sand-400"></i>
          </motion.div>
        </motion.div>
      </section>

      {/* Por que viajar em grupo */}
      <Section bgColor="sand">
        <div className="max-w-5xl mx-auto">
          <FadeInUp className="text-center mb-16">
            <span className="inline-flex items-center gap-2 bg-olive-100 text-olive-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <i className="ri-lightbulb-line"></i>
              Por que em grupo?
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-earth-800 mb-4">
              Viagem de mochileiro<br />
              <span className="text-olive-600">sem a parte difícil</span>
            </h2>
            <p className="text-earth-600 max-w-2xl mx-auto text-lg">
              A gente cuida do roteiro, reservas e logística. Você só precisa 
              curtir a viagem e praticar seu inglês.
            </p>
          </FadeInUp>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {TRANSFORMATION_BENEFITS.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="text-center h-full">
                  <div className="w-14 h-14 bg-olive-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <i className={`${benefit.icon} text-2xl text-olive-600`}></i>
                  </div>
                  <p className="text-3xl font-bold text-olive-600 mb-1">{benefit.stat}</p>
                  <h3 className="font-semibold text-earth-800 mb-1">{benefit.title}</h3>
                  <p className="text-earth-500 text-sm">{benefit.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Transformation quote */}
          <motion.div 
            className="mt-16 bg-gradient-to-r from-olive-600 to-olive-700 rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="absolute top-4 right-8 text-8xl text-white/10">
              <i className="ri-double-quotes-r"></i>
            </div>
            <p className="text-xl md:text-2xl font-light italic mb-6 relative z-10 max-w-3xl mx-auto">
              "Hostel, transporte público, comida de rua... quando você viaja 
              como mochileiro, precisa se virar em inglês. É assim que se aprende 
              <strong>de verdade</strong>."
            </p>
            <div className="flex items-center justify-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-user-smile-line text-xl"></i>
              </div>
              <div className="text-left">
                <p className="font-semibold">Elienai Santos</p>
                <p className="text-olive-200 text-sm">Fundadora da Beyond Class</p>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Expedições - Mapa Interativo */}
      <Section bgColor="white" id="expeditions">
        <FadeInUp className="text-center mb-12">
          <span className="inline-flex items-center gap-2 bg-olive-100 text-olive-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-road-map-line"></i>
            Nossas expedições
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-earth-800 mb-4">
            Onde já levamos nossos alunos<br />
            <span className="text-olive-600">para aprender inglês</span>
          </h2>
          <p className="text-earth-600 max-w-2xl mx-auto text-lg">
            Cada expedição é uma aventura única. Explore nossos roteiros!
          </p>
        </FadeInUp>

        {/* Expedition Selector */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {EXPEDITIONS.map((exp) => (
            <motion.button
              key={exp.id}
              onClick={() => setSelectedExpedition(exp)}
              className={`px-4 sm:px-6 py-3 rounded-xl font-medium transition-all ${
                selectedExpedition.id === exp.id 
                  ? 'bg-olive-600 text-white shadow-lg' 
                  : 'bg-sand-100 text-earth-700 hover:bg-sand-200'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="flex items-center gap-2">
                <i className="ri-compass-3-line"></i>
                {exp.name}
              </span>
              <span className={`text-xs ${selectedExpedition.id === exp.id ? 'text-white/70' : 'text-earth-500'}`}>
                {exp.date}
              </span>
            </motion.button>
          ))}
        </div>

        {/* Map + Details */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedExpedition.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 lg:grid-cols-5 gap-8"
          >
            {/* Map */}
            <div className="lg:col-span-3 h-[500px] rounded-2xl overflow-hidden shadow-xl">
              <MapContainer
                center={selectedExpedition.route[0].coords}
                zoom={5}
                className="h-full w-full"
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <AnimatedRoute route={selectedExpedition.route} color={selectedExpedition.color} />
              </MapContainer>
            </div>

            {/* Route Details */}
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-sand-50 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-earth-800 mb-4 flex items-center gap-2">
                  <i className="ri-route-line text-olive-600"></i>
                  Roteiro completo
                </h3>
                <div className="space-y-3">
                  {selectedExpedition.route.map((stop, index) => (
                    <div 
                      key={stop.name}
                      className="flex items-start gap-3 p-3 bg-white rounded-xl"
                    >
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
                        style={{ backgroundColor: selectedExpedition.color }}
                      >
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-earth-800">{stop.name}</h4>
                        <p className="text-earth-500 text-sm mt-1">{stop.highlight}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>


            </div>
          </motion.div>
        </AnimatePresence>

        {/* Photo Gallery - Simple masonry style */}
        <motion.div 
          className="mt-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          {/* Gallery header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-earth-800 flex items-center gap-2">
              <i className="ri-camera-line text-olive-600"></i>
              Fotos dos Intercâmbios
            </h3>
            <button 
              onClick={() => {
                setActiveGalleryImage(0);
                setShowLightbox(true);
              }}
              className="flex items-center gap-2 text-olive-600 hover:text-olive-700 font-medium text-sm transition-colors"
            >
              Abrir galeria
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>

          {/* Gallery grid - 3 columns with varied heights */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {selectedExpedition.gallery.map((img, idx) => (
              <motion.button
                key={idx}
                onClick={() => {
                  setActiveGalleryImage(idx);
                  setShowLightbox(true);
                }}
                className={`relative rounded-xl overflow-hidden group ${
                  idx === 0 ? 'md:row-span-2 aspect-[3/4] md:aspect-auto' : 'aspect-[4/3]'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <img 
                  src={img} 
                  alt="" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-earth-900/0 group-hover:bg-earth-900/30 transition-colors flex items-center justify-center">
                  <i className="ri-zoom-in-line text-2xl text-white opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </Section>
      {/* CTA Final */}
      <Section bgColor="dark">
        <FadeInUp className="text-center max-w-3xl mx-auto">
          <div className="w-20 h-20 bg-olive-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-suitcase-3-line text-3xl text-white"></i>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-sand-100 mb-4">
            Sua próxima aventura<br />
            <span className="text-olive-400">começa aqui</span>
          </h2>
          <p className="text-sand-300 text-lg max-w-xl mx-auto mb-8 leading-relaxed">
            Não é só uma viagem. É uma experiência que vai transformar 
            seu inglês e sua vida. Venha ser um mochileiro Beyond Class.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              to="/contato" 
              size="lg" 
              className="bg-olive-500 hover:bg-olive-400 text-white"
              icon="ri-rocket-line"
            >
              Quero viajar!
            </Button>
          </div>
        </FadeInUp>
      </Section>

      {/* Enhanced Lightbox */}
      <AnimatePresence>
        {showLightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-earth-900/98 z-50 flex items-center justify-center"
            onClick={() => setShowLightbox(false)}
          >
            {/* Background blur */}
            <div className="absolute inset-0 backdrop-blur-sm" />
            
            {/* Image container */}
            <div className="relative w-full h-full flex items-center justify-center p-4 md:p-12">
              <AnimatePresence mode="wait">
                <motion.img
                  key={activeGalleryImage}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  src={selectedExpedition.gallery[activeGalleryImage]}
                  alt=""
                  className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                />
              </AnimatePresence>
            </div>

            {/* Navigation arrows */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setActiveGalleryImage(prev => prev === 0 ? selectedExpedition.gallery.length - 1 : prev - 1);
              }}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all hover:scale-110"
            >
              <i className="ri-arrow-left-s-line text-2xl text-white"></i>
            </button>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setActiveGalleryImage(prev => (prev + 1) % selectedExpedition.gallery.length);
              }}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all hover:scale-110"
            >
              <i className="ri-arrow-right-s-line text-2xl text-white"></i>
            </button>
            
            {/* Close button */}
            <button 
              onClick={() => setShowLightbox(false)}
              className="absolute top-4 right-4 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              <i className="ri-close-line text-2xl text-white"></i>
            </button>
            
            {/* Bottom bar with thumbnails */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-earth-900/80 backdrop-blur-md rounded-2xl p-3 flex items-center gap-3">
              {selectedExpedition.gallery.map((img, idx) => (
                <motion.button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveGalleryImage(idx);
                  }}
                  className={`w-16 h-12 rounded-lg overflow-hidden transition-all ${
                    activeGalleryImage === idx 
                      ? 'ring-2 ring-olive-400 scale-110' 
                      : 'opacity-60 hover:opacity-100'
                  }`}
                  whileHover={{ scale: activeGalleryImage === idx ? 1.1 : 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </motion.button>
              ))}
            </div>

            {/* Counter */}
            <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-earth-900/70 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
              {activeGalleryImage + 1} / {selectedExpedition.gallery.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
